// Update Template DB: makes TMPLT's ISA Templates an exact mirror of the library's Published ISA
// Templates. The preview reads only; the update asks first and is all or nothing.
import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, DatabaseZap } from 'lucide-react';
import { dsMasterCatalogApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { dsErrMsg } from './dsBoxes';
import { ConfirmModal } from './CatalogModals';

const ACTION_BADGE = {
  insert: 'stui-badge-green',
  update: 'stui-badge-amber',
  unchanged: 'stui-badge-gray',
  conflict: 'stui-badge-red',
};
const actionBadge = a => ACTION_BADGE[a] ?? 'stui-badge-gray';

export default function TemplateDbSection({ nav = null }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPreview((await dsMasterCatalogApi.previewTemplateDb()).data); }
    catch (e) { notify.error(dsErrMsg(e, 'Failed to read the template database.')); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const templates = preview?.templates ?? [];
  const removals = preview?.removals ?? [];
  const count = a => templates.filter(t => t.action === a).length;
  const conflicts = count('conflict');
  const pending = count('insert') + count('update') + removals.length;

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await dsMasterCatalogApi.updateTemplateDb(auditCfg);
      setLastRun(res.data);
      notify.success(`Template database updated (${res.data.database}).`);
      setConfirming(false);
      await load();
    } catch (e) { notify.error(dsErrMsg(e, 'Update Template DB failed. Nothing was changed.')); }
    finally { setRunning(false); }
  };

  return (
    <>
      <div className="stui-page-header">
        <div className="stui-row-8">
          {nav}
          <span className="stui-fw-semibold">{preview?.database ?? 'Template database'}</span>
          {preview && (
            <span className="stui-text-sm-muted">
              {`${templates.length} Published ISA Template${templates.length === 1 ? '' : 's'}: ${count('insert')} to add, ${count('update')} to refresh, ${count('unchanged')} unchanged, ${conflicts} in conflict; ${removals.length} to remove.`}
            </span>
          )}
        </div>
        <div className="stui-row-8">
          <button className="stui-btn stui-btn-secondary" onClick={load} disabled={loading} title="Refresh preview"><RefreshCw size={14} /></button>
          <button className="stui-btn stui-btn-primary" onClick={() => setConfirming(true)}
            disabled={loading || !preview || conflicts > 0 || pending === 0}
            title={conflicts > 0 ? 'Resolve the conflicts first' : pending === 0 ? 'Already in step' : undefined}>
            <DatabaseZap size={14} /> Update Template DB…
          </button>
        </div>
      </div>

      <div className="stui-detail-fill stui-mt-sm">
        <p className="stui-hint">
          Only Published ISA Templates are mirrored, boxes included, with the same IDs. Index Sheets, manufacturers,
          Index Sheet Datasheets and values are never written to the template database. A template still referenced
          there is kept and reported.
        </p>
        {loading && !preview ? (
          <div className="stui-detail-empty">Loading…</div>
        ) : (
          <div className="stui-data-grid-container">
            <div className="stui-data-grid-scroll-area">
              <table className="stui-data-grid stui-data-grid-auto">
                <thead>
                  <tr><th>ID</th><th>Template</th><th>Action</th><th>Detail</th></tr>
                </thead>
                <tbody>
                  {templates.length === 0 && removals.length === 0 && (
                    <tr><td colSpan={4} className="stui-td-empty">No Published ISA Templates yet. Publish them one by one in the Template Composer.</td></tr>
                  )}
                  {templates.map(t => (
                    <tr key={t.id}>
                      <td>{t.id}</td>
                      <td className="stui-mono">{t.dsIndex}</td>
                      <td><span className={`stui-badge ${actionBadge(t.action)}`}>{t.action}</span></td>
                      <td className="stui-td-xs">
                        {t.action === 'conflict' ? t.error
                          : t.action === 'update' ? [
                              t.indexFields?.length ? `fields: ${t.indexFields.join(', ')}` : null,
                              t.boxesMissing ? `${t.boxesMissing} boxes to add` : null,
                              t.boxesChanged ? `${t.boxesChanged} boxes changed` : null,
                              t.boxesOnlyInTarget ? `${t.boxesOnlyInTarget} boxes to remove` : null,
                            ].filter(Boolean).join('; ')
                          : ''}
                      </td>
                    </tr>
                  ))}
                  {removals.map(r => (
                    <tr key={`rm-${r.id}`}>
                      <td>{r.id}</td>
                      <td className="stui-mono">{r.dsIndex}</td>
                      <td><span className="stui-badge stui-badge-red">remove</span></td>
                      <td className="stui-td-xs">Not a Published ISA Template in the library</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {lastRun && (
          <div className="stui-inline-notice stui-inline-notice-success stui-mt-sm">
            {`Last run: ${(lastRun.templates ?? []).filter(t => t.outcome !== 'Unchanged')
              .map(t => `${t.id} ${t.outcome}${t.note ? ` (${t.note})` : ''}`).join(', ') || 'nothing to change'}.`}
          </div>
        )}
      </div>

      {confirming && (
        <ConfirmModal title="Update Template DB" confirmLabel="Update" busy={running}
          onConfirm={handleRun} onClose={() => setConfirming(false)}>
          <p className="stui-hint">
            {`${preview?.database} will be made to match the library: ${count('insert')} template(s) added, ${count('update')} refreshed, ${removals.length} removed. `}
            New projects copied from it start with exactly these ISA Templates.
          </p>
          <div className="stui-inline-notice stui-inline-notice-warning">This writes to the template database every new project is created from.</div>
        </ConfirmModal>
      )}
    </>
  );
}
